import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import Header from "./Front/Header";
import Footer from "./Front/Footer";
import API_BASE_URL from "../Config";
import Pagination from "./Pagination";
import PriceRangePicker from "./PriceRangePicker";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";
import { requireLogin } from "../utils/authRedirect";
import { isDoctorPublic } from "../utils/doctorVisibility";
import "../styles/global.css";
import "./styles/home.css";
import "./styles/FrontDoctorBooking.css";

const FrontDoctorBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState(localStorage.getItem("user_id"));
  if (!userId) { requireLogin(navigate, location, { message: "Please login to book a consultation" }); }
  // Safe read: older admin logins stored "[object Object]" instead of JSON
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      localStorage.removeItem("user");
      return null;
    }
  })();
  const [userData, setUserData] = useState(user);
  const doctor_id = location.state?.doctor_id;
  const doctor_slug = location.state?.doctor_slug;
  const booking_date = location.state?.booking_date;
  const booking_time = location.state?.booking_time;
  const visitType = location.state?.visitType;
  const fees = location.state?.fees;
  const [doctor, setDoctor] = useState(null);
  const [imagesSwip, setImages] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [patientName, setPatientName] = useState(userData?.name);
  const [mobile, setMobile] = useState(userData?.mobile);

  // ── Coupon ─────────────────────────────────────────────────
  const authToken = localStorage.getItem("authToken");
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showAllCoupons, setShowAllCoupons] = useState(false);

  const originalFees = Number(fees) || 0;
  const finalFees = Math.max(0, originalFees - couponDiscount);

  // Coupons this user has already redeemed are excluded entirely — the
  // backend still rejects (with a toast message) if one is typed manually.
  const visibleCoupons = availableCoupons.filter((c) => !c.already_used);

  const fetchAvailableCoupons = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}api/coupons_front`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      const data = await res.json();
      if (data.status) setAvailableCoupons(data.data || []);
    } catch (err) {
      console.error("Error fetching coupons:", err);
    }
  };

  useEffect(() => { fetchAvailableCoupons(); }, []);

  const applyCoupon = async (codeOverride) => {
    const code = (codeOverride || couponInput).trim().toUpperCase();
    if (!code) { toast.error("Enter a coupon code"); return; }
    if (!authToken) { toast.error("Please login to apply a coupon"); return; }
    setCouponLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}api/applyCoupon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ coupon: code, amount: originalFees, for: "doctor_booking" }),
      });
      const data = await res.json();
      if (data.status) {
        setAppliedCoupon(code);
        setCouponDiscount(Number(data.discount));
        toast.success("Coupon applied!");
      } else {
        toast.error(data.message || "Invalid coupon code");
      }
    } catch {
      toast.error("Failed to apply coupon");
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponInput("");
    toast.success("Coupon removed");
  };

  // ─────────────────────────────────────────────────────────────
  // ✅ CHANGE #1 — Add a "submitting" flag to prevent double-clicks
  // Why: If user clicks "Confirm & Pay" twice quickly, two bookings
  // could be created. This flag disables the button during submission.
  // ─────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchDoctor = async (slug) => {
    const response = await fetch(`${API_BASE_URL}api/doctor/${slug}`);
    const json = await response.json();
    if (json.status) {
      setDoctor(json.data);
      const images = json.data.images ? json.data.images.split(",") : [];
      setImages(images);
    }
  };

  useEffect(() => { fetchDoctor(doctor_slug); }, [doctor_slug]);

  // ─────────────────────────────────────────────────────────────
  // ✅ CHANGE #2 — Refactored handleBookDoctor with proper payment flow
  //
  // PROBLEM (before): The booking was treated as "confirmed" the moment
  // /api/bookDoctor was called. If the user pressed BACK on the payment
  // page, the appointment was already in the database.
  //
  // FIX (now):
  //   • Validate inputs FIRST (before hitting any API)
  //   • If fees === 0 → skip payment gateway entirely, go to success page
  //     where /api/verify-booking-order will finalize the booking.
  //   • If fees > 0 → call /api/bookDoctor (which should create booking
  //     as PENDING on backend — see note below) → open Cashfree.
  //   • Cashfree's redirectTarget="_self" means after payment (success
  //     OR failure OR back button), the user lands on the success URL
  //     configured in your Cashfree dashboard, which should point to
  //     /order-booking-success. That page calls verify-booking-order,
  //     which is the ONLY place a booking should be marked PAID.
  //
  // ⚠️ BACKEND CONTRACT (one-time check, not a code change here):
  //   Your /api/bookDoctor must save the booking with status = "PENDING"
  //   (not "CONFIRMED"). Your /api/verify-booking-order must flip it
  //   to "CONFIRMED" only when Cashfree returns order_status === "PAID".
  //   If your backend already does this, you're done. If it doesn't,
  //   that's the one backend tweak required — frontend alone can't fix it.
  // ─────────────────────────────────────────────────────────────
  const handleBookDoctor = async () => {
    // Guard: prevent double submission
    if (isSubmitting) return;

    // Guard: basic validation BEFORE creating any booking record
    if (!patientName || !patientName.trim()) {
      toast.error("Please enter patient name");
      return;
    }
    if (!mobile || !String(mobile).trim()) {
      toast.error("Please enter mobile number");
      return;
    }
    if (!doctor_id || !booking_date || !booking_time) {
      toast.error("Booking details are incomplete. Please reselect a slot.");
      return;
    }

    setIsSubmitting(true);

    const bookingData = {
      doctor_id,
      booking_date,
      booking_time,
      patient_name: patientName,
      mobile,
      patient_id: userId,
      booking_type: visitType,
      fees: originalFees,
      coupon: appliedCoupon || undefined,
    };

    try {
      // ─────────────────────────────────────────────────────────
      // ✅ CHANGE #3 — Zero-fee shortcut
      // If the consultation is free, there's no payment gateway to go
      // through. We still call the SAME /api/bookDoctor API (so the
      // backend contract is unchanged), but we skip Cashfree and route
      // straight to the success page, where verify-booking-order can
      // finalize. We pass a flag in the URL so the success page knows
      // it's a free booking.
      // ─────────────────────────────────────────────────────────
      if (finalFees <= 0) {
        const response = await fetch(`${API_BASE_URL}api/bookDoctor`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ ...bookingData, fees: originalFees }),
        });
        const result = await response.json();

        if (!response.ok || !result.status) {
          toast.error(result?.message || "Booking failed");
          setIsSubmitting(false);
          return;
        }

        // ─────────────────────────────────────────────────────────
        // ✅ CHANGE #3a — Robust ID extraction for free bookings
        // Different backends return the ID under different keys. We
        // check every common shape so we never end up with "undefined"
        // in the URL. If still nothing, fall back to a placeholder so
        // the success page can still render (it now treats free=1 as
        // a verified-success signal, regardless of order_id).
        // ─────────────────────────────────────────────────────────
        const orderId =
          result.order_id ||
          result.orderId ||
          result.booking_id ||
          result.bookingId ||
          result.id ||
          result.data?.order_id ||
          result.data?.booking_id ||
          result.data?.id ||
          "FREE-" + Date.now(); // last-resort placeholder

        toast.success("Appointment booked successfully");
        navigate(`/order-booking-success?order_id=${orderId}&free=1`);
        return;
      }

      // ─────────────────────────────────────────────────────────
      // PAID FLOW — unchanged API call, just better error handling
      // ─────────────────────────────────────────────────────────
      const response = await fetch(`${API_BASE_URL}api/bookDoctor`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(bookingData),
      });
      const result = await response.json();

      if (!response.ok) {
        toast.error(result?.message || "Order failed");
        setIsSubmitting(false);
        return;
      }

      if (result.status) {
        // ✅ CHANGE #4 — Cashfree redirect
        // Cashfree will redirect back to the URL configured in your
        // dashboard (must be /#/order-booking-success?order_id=XXX).
        // The success page is what verifies payment and finalizes the
        // booking. NO appointment-creation logic should live here.
        const cashfree = window.Cashfree({ mode: "production" });
        cashfree.checkout({
          paymentSessionId: result.payment_session_id,
          redirectTarget: "_self",
        });
        // Note: we do NOT call setIsSubmitting(false) here because the
        // page is about to navigate away. If checkout() fails to launch,
        // the catch block will reset it.
      } else {
        toast.error(result.message);
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  const formattedDate = booking_date
    ? new Date(booking_date).toLocaleDateString("en-US", {
        weekday: "short", year: "numeric", month: "short", day: "numeric",
      })
    : "";

  // Blocks the booking flow itself in case a stale link, cached button, or
  // an already-open tab still points at a doctor who has since been turned
  // Inactive/un-approved by an admin.
  if (doctor && !isDoctorPublic(doctor)) {
    return (
      <>
        <Header />
        <div className="bk-page pb-5">
          <div className="container py-5 text-center">
            <h3 className="mb-2">This doctor is currently unavailable</h3>
            <p className="text-muted mb-4">Bookings can't be made for this profile right now.</p>
            <Link to="/doctor-list-page" className="btn btn-main btn-css-1 book-btn rounded-pill">
              Browse other doctors
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      <div className="bk-page pb-5">
        <div className="container">

          {/* Breadcrumb */}
          <div className="bk-bread">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="index.html">Home</a></li>
                <li className="breadcrumb-item active" aria-current="page">Appointment Booking</li>
              </ol>
            </nav>
          </div>

          {/* ═══ TWO COLUMN LAYOUT ═══ */}
          <div className="bk-layout">

            {/* ──────────────────────────
               LEFT — DOCTOR INFO
            ────────────────────────── */}
            <div className="bk-left">
              <div className="bk-card">

                {/* Doctor hero */}
                <div className="bk-doc-hero">
                  <div className="bk-doc-avatar">
                    <img
                      src={doctor?.images?.length
                        ? `${API_BASE_URL}public/doctorimages/${doctor.images.split(",")[0]}`
                        : `${API_BASE_URL}public/noimage.jpg`}
                      alt={doctor?.name || "Doctor"}
                    />
                  </div>
                  <h3 className="bk-doc-name">{doctor?.name}</h3>
                  <div className="bk-doc-dept">
                    <i className="fa-solid fa-stethoscope" />
                    {doctor?.department_title?.name}
                  </div>
                  <div className="bk-visit-badge">
                    <i className={visitType === "video" ? "fa-solid fa-video" : "fa-solid fa-hospital"} />
                    {visitType === "video" ? "Video Consult" : "In-Clinic Visit"}
                  </div>
                </div>

                {/* Doctor details */}
                <div className="bk-doc-details">

                  {/* Specialization */}
                  {doctor?.specializations && (
                    <div className="bk-detail-row">
                      <div className="bk-detail-icon"><i className="fa-solid fa-award" /></div>
                      <div className="bk-detail-content">
                        <div className="bk-detail-label">Specialization</div>
                        <div className="bk-detail-value">{doctor.specializations}</div>
                      </div>
                    </div>
                  )}

                  {/* Experience */}
                  {doctor?.experience && (
                    <div className="bk-detail-row">
                      <div className="bk-detail-icon"><i className="fa-solid fa-briefcase-medical" /></div>
                      <div className="bk-detail-content">
                        <div className="bk-detail-label">Experience</div>
                        <div className="bk-detail-value">{doctor.experience}</div>
                      </div>
                    </div>
                  )}

                  {/* Clinic */}
                  <div className="bk-detail-row">
                    <div className="bk-detail-icon">
                      {doctor?.clinicImage ? (
                        <div className="bk-clinic-thumb">
                          <img src={`${API_BASE_URL}public/clinic/${doctor.clinicImage.split(",")[0]}`} alt="" />
                        </div>
                      ) : (
                        <i className="fa-solid fa-hospital" />
                      )}
                    </div>
                    <div className="bk-detail-content">
                      <div className="bk-detail-label">Clinic</div>
                      <div className="bk-detail-value">
                        {doctor?.address_org ? (
                          <>
                            {doctor.address_org}
                            <br />
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doctor.address_org || "")}`}
                              target="_blank" rel="noopener noreferrer">
                              <i className="fa-solid fa-location-arrow" /> Get Directions
                            </a>
                          </>
                        ) : (
                          <>
                            {doctor?.clinics}
                            <br />
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doctor?.address || "")}`}
                              target="_blank" rel="noopener noreferrer">
                              <i className="fa-solid fa-location-arrow" /> Get Directions
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  {doctor?.address && (
                    <div className="bk-detail-row">
                      <div className="bk-detail-icon"><i className="fa-solid fa-map-pin" /></div>
                      <div className="bk-detail-content">
                        <div className="bk-detail-label">Address</div>
                        <div className="bk-detail-value">{doctor.address}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Fee */}
                <div className="bk-fee-bar" style={{ flexDirection: "column", alignItems: "stretch", gap: "6px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span className="bk-fee-label">Consultation Fee</span>
                    <span className="bk-fee-amount">₹ {originalFees}</span>
                  </div>
                  {couponDiscount > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", color: "#1a7a4a", fontSize: "14px" }}>
                      <span>Coupon Discount ({appliedCoupon})</span>
                      <span>- ₹ {couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  {couponDiscount > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderTop: "1px dashed #ddd", paddingTop: "6px" }}>
                      <span className="bk-fee-label">Final Payable Amount</span>
                      <span className="bk-fee-amount">₹ {finalFees.toFixed(2)}</span>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* ──────────────────────────
               RIGHT — BOOKING FORM
            ────────────────────────── */}
            <div>
              <div className="bk-card">

                <div className="bk-right-header">
                  <h2><i className="fa-solid fa-calendar-check" /> Book Appointment</h2>
                </div>

                {/* Date & Time */}
                <div className="bk-datetime">
                  <div className="bk-dt-box">
                    <div className="bk-dt-icon"><i className="fa-regular fa-calendar" /></div>
                    <div className="bk-dt-text">
                      <span className="bk-dt-label">Date</span>
                      <span className="bk-dt-value">{formattedDate}</span>
                      <button className="bk-change" onClick={() => navigate(-1)}>
                        <i className="fa-solid fa-pen" /> Change
                      </button>
                    </div>
                  </div>
                  <div className="bk-dt-box">
                    <div className="bk-dt-icon"><i className="fa-regular fa-clock" /></div>
                    <div className="bk-dt-text">
                      <span className="bk-dt-label">Time</span>
                      <span className="bk-dt-value">{booking_time}</span>
                    </div>
                  </div>
                </div>

                {/* Patient Form */}
                <div className="bk-form">

                  <div className="bk-field">
                    <label><i className="fa-solid fa-user" /> Full Name <span>*</span></label>
                    <input type="text" name="name" value={patientName || ""}
                      onChange={(e) => setPatientName(e.target.value)} placeholder="Enter patient name" />
                  </div>

                  <div className="bk-field">
                    <label><i className="fa-solid fa-mobile-screen" /> Mobile <span>*</span></label>
                    <div className="bk-mobile-wrap">
                      <input type="text" name="mobile" value={mobile || ""}
                        onChange={(e) => setMobile(e.target.value)} placeholder="Enter mobile number" />
                      <i className="fa-solid fa-lock" />
                    </div>
                  </div>

                  <div className="bk-field">
                    <label><i className="fa-solid fa-envelope" /> Email</label>
                    <input type="email" name="email" value={userData?.email || ""} placeholder="Enter email (optional)" />
                  </div>

                  {/* ── Coupon ─────────────────────────────────────── */}
                  <div className="bk-field">
                    <label><i className="fa-solid fa-tag" /> Have a Coupon?</label>
                    {!appliedCoupon ? (
                      <div style={{ display: "flex", gap: "8px" }}>
                        <input
                          type="text"
                          placeholder="Enter coupon code"
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                          onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }}
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="bk-change"
                          onClick={() => applyCoupon()}
                          disabled={couponLoading}
                        >
                          {couponLoading ? <i className="fa-solid fa-spinner fa-spin" /> : "Apply"}
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>
                          <i className="fa-solid fa-circle-check" style={{ color: "#1a7a4a", marginRight: "6px" }} />
                          <b>{appliedCoupon}</b> applied — you saved ₹{couponDiscount.toFixed(2)}
                        </span>
                        <button type="button" className="bk-change" onClick={removeCoupon}>Remove</button>
                      </div>
                    )}

                    {!appliedCoupon && (
                      <button
                        type="button"
                        className="bk-change"
                        style={{ marginTop: "10px" }}
                        onClick={() => setShowAllCoupons(true)}
                      >
                        <i className="fa-solid fa-tags" /> View All Coupons
                        {visibleCoupons.length > 0 ? ` (${visibleCoupons.length})` : ""}
                      </button>
                    )}
                  </div>

                  {showAllCoupons && (
                    <div
                      onClick={() => setShowAllCoupons(false)}
                      style={{
                        position: "fixed", inset: 0, background: "rgba(20,30,26,.55)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 1050, padding: "16px",
                      }}
                    >
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          background: "#fff", borderRadius: "16px", width: "100%",
                          maxWidth: "420px", maxHeight: "80vh", overflowY: "auto",
                          padding: "18px", boxShadow: "0 20px 50px rgba(0,0,0,.25)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                          <p style={{ margin: 0, fontWeight: 700 }}>Available Coupons</p>
                          <button
                            type="button"
                            onClick={() => setShowAllCoupons(false)}
                            aria-label="Close"
                            style={{
                              background: "#f1f1f1", border: "none", borderRadius: "50%",
                              width: "30px", height: "30px", cursor: "pointer",
                            }}
                          >
                            <i className="fa-solid fa-xmark" />
                          </button>
                        </div>
                        {visibleCoupons.length === 0 ? (
                          <p style={{ textAlign: "center", color: "#8a9490", fontSize: "13px", padding: "24px 8px", margin: 0 }}>
                            No coupons available right now. Check back later!
                          </p>
                        ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          {visibleCoupons.map((c) => (
                            <div key={c.code}
                              style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                border: "1px dashed #b3e6ca", borderRadius: "8px", padding: "8px 10px",
                                background: "#f6fcf9",
                              }}>
                              <div>
                                <div style={{ fontWeight: 700, color: "#1a7a4a" }}>{c.code}</div>
                                <div style={{ fontSize: "12px", color: "#5b6b66" }}>
                                  {c.type === "percent" ? `${c.discount}% off` : `₹${c.discount} off`} · Min order ₹{c.min_order}
                                </div>
                              </div>
                              <button
                                type="button"
                                className="bk-change"
                                onClick={() => { applyCoupon(c.code); setShowAllCoupons(false); }}
                              >
                                Apply
                              </button>
                            </div>
                          ))}
                        </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ✅ CHANGE #5 — disabled state + dynamic label while submitting */}
                  <button
                    className="bk-confirm-desk"
                    onClick={handleBookDoctor}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin" /> Processing…
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-shield-check" />
                        {finalFees <= 0
                          ? "Confirm Appointment (Free)"
                          : `Confirm & Pay ₹${finalFees.toFixed(2)}`}
                      </>
                    )}
                  </button>

                  <div className="bk-terms">
                    <p>Updates will be sent to your registered mobile number.</p>
                    <p>By booking, you agree to our <a href="#terms-condition">Terms & Conditions</a>.</p>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ═══ MOBILE STICKY ═══ */}
      <div className="bk-sticky">
        <div className="bk-sticky-left">
          <span className="bk-sticky-price">₹ {finalFees.toFixed(2)}</span>
          <span className="bk-sticky-sub">
            {couponDiscount > 0 ? "After coupon discount" : "Consultation fee"}
          </span>
        </div>
        {/* ✅ CHANGE #6 — same disabled-state on mobile sticky button */}
        <button
          className="bk-sticky-btn"
          onClick={handleBookDoctor}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" /> Processing…
            </>
          ) : (
            <>
              <i className="fa-solid fa-shield-check" />
              {finalFees <= 0 ? "Confirm" : "Confirm & Pay"}
            </>
          )}
        </button>
      </div>

      <Footer />
    </>
  );
};

export default FrontDoctorBooking;